import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { fetchCrossProjectIssues } from "@/lib/api";
import KanbanBoard from "@/components/board/KanbanBoard";
import IssuePanel from "@/components/issue/IssuePanel";
import {
  ISSUE_TYPES,
  ISSUE_TYPE_CONFIG,
  ENVIRONMENTS,
  ENV_LABELS,
  ENV_COLORS,
} from "@/lib/constants";
import { formatAssignmentFocus } from "@/components/users/AssignmentFocus";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

export default function CrossProjectPage({ users, projects, currentUser }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIssueKey, setSelectedIssueKey] = useState(null);

  // Filters
  const [filterType, setFilterType] = useState("ALL");
  const [filterAssignee, setFilterAssignee] = useState("ALL");
  const [filterProject, setFilterProject] = useState("ALL");
  const [filterEnvs, setFilterEnvs] = useState(["EPH", "DEV", "PROD"]);

  const loadIssues = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterType !== "ALL") params.type = filterType;
      if (filterAssignee !== "ALL") params.assignee_id = filterAssignee;
      if (filterProject !== "ALL") params.project_key = filterProject;
      if (filterType === "BUG" && filterEnvs.length < 3 && filterEnvs.length > 0) {
        params.environment = filterEnvs.join(",");
      }
      const data = await fetchCrossProjectIssues(params);
      setIssues(data.items || data);
    } catch (e) {
      console.error("Failed to load cross-project issues", e);
    } finally {
      setLoading(false);
    }
  }, [filterType, filterAssignee, filterProject, filterEnvs]);

  useEffect(() => {
    loadIssues();
  }, [loadIssues]);

  // Reset assignee when type changes (so we don't have a stale selection)
  useEffect(() => {
    setFilterAssignee("ALL");
  }, [filterType]);

  useEffect(() => {
    const issueParam = searchParams.get("issue");
    if (issueParam) setSelectedIssueKey(issueParam);
  }, [searchParams]);

  const handleSelectIssue = (issueKey) => {
    setSelectedIssueKey(issueKey);
    setSearchParams({ issue: issueKey });
  };

  const handleClosePanel = () => {
    setSelectedIssueKey(null);
    setSearchParams({});
  };

  const handleIssueDeleted = () => {
    handleClosePanel();
    loadIssues();
  };

  const toggleEnv = (env) => {
    setFilterEnvs((prev) =>
      prev.includes(env) ? prev.filter((e) => e !== env) : [...prev, env]
    );
  };

  const handleOptimisticStatusChange = useCallback((issueId, newStatus) => {
    setIssues((prev) =>
      prev.map((i) => (i.id === issueId ? { ...i, status: newStatus } : i))
    );
  }, []);

  const showBugEnvFilter = filterType === "BUG";

  return (
    <div className="h-full flex flex-col overflow-hidden" data-testid="cross-project-page">
      {/* Header + Filter Bar */}
      <div className="px-6 py-4 border-b border-gray-200 bg-white flex-shrink-0 space-y-3">
        <h2
          className="text-base font-bold text-black"
          style={{ fontFamily: "Chivo, sans-serif" }}
          data-testid="cross-project-title"
        >
          Cross Project
          <span className="text-xs text-gray-400 font-normal ml-2">
            {issues.length} issues
          </span>
        </h2>

        {/* Filter Bar */}
        <div className="flex items-center gap-3 flex-wrap">
          {/* Type Filter */}
          <div className="flex items-center gap-1.5">
            <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-gray-500">
              Type
            </label>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger
                className="rounded-sm h-8 text-xs w-[140px]"
                data-testid="filter-type-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-sm">
                <SelectItem value="ALL" className="rounded-sm text-xs">
                  All Types
                </SelectItem>
                {ISSUE_TYPES.map((t) => {
                  const cfg = ISSUE_TYPE_CONFIG[t] || {};
                  return (
                    <SelectItem key={t} value={t} className="rounded-sm text-xs">
                      <div className="flex items-center gap-2">
                        <span
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: cfg.text }}
                        />
                        {cfg.label}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {/* Assignee Filter */}
          <div className="flex items-center gap-1.5">
            <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-gray-500">
              Assignee
            </label>
            <Select value={filterAssignee} onValueChange={setFilterAssignee}>
              <SelectTrigger
                className="rounded-sm h-8 text-xs w-[160px]"
                data-testid="filter-assignee-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-sm">
                <SelectItem value="ALL" className="rounded-sm text-xs">
                  All Assignees
                </SelectItem>
                {(users || []).map((u) => {
                  const focus = formatAssignmentFocus(u.assignment_focus);
                  return (
                    <SelectItem key={u.id} value={u.id} className="rounded-sm text-xs">
                      <div className="flex items-center gap-2">
                        <img
                          src={u.avatar_url}
                          alt={u.full_name}
                          className="w-4 h-4 rounded-full"
                        />
                        {u.full_name}
                        {focus && (
                          <span className="text-[10px] text-gray-400">({focus})</span>
                        )}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {/* Project Filter */}
          <div className="flex items-center gap-1.5">
            <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-gray-500">
              Project
            </label>
            <Select value={filterProject} onValueChange={setFilterProject}>
              <SelectTrigger
                className="rounded-sm h-8 text-xs w-[160px]"
                data-testid="filter-project-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-sm">
                <SelectItem value="ALL" className="rounded-sm text-xs">
                  All Projects
                </SelectItem>
                {projects.map((p) => (
                  <SelectItem key={p.key} value={p.key} className="rounded-sm text-xs">
                    <span className="font-mono text-gray-400 mr-1">{p.key}</span>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Bug Environment Filter */}
          {showBugEnvFilter && (
            <div className="flex items-center gap-2 ml-2 pl-2 border-l border-gray-200">
              <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-gray-500">
                Env
              </label>
              {ENVIRONMENTS.map((env) => {
                const envColor = ENV_COLORS[env] || {};
                return (
                  <label
                    key={env}
                    className="flex items-center gap-1.5 cursor-pointer"
                  >
                    <Checkbox
                      data-testid={`filter-env-${env.toLowerCase()}`}
                      checked={filterEnvs.includes(env)}
                      onCheckedChange={() => toggleEnv(env)}
                      className="rounded-sm h-3.5 w-3.5"
                    />
                    <span
                      className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm border"
                      style={{
                        backgroundColor: envColor.bg,
                        color: envColor.text,
                        borderColor: envColor.border,
                      }}
                    >
                      {env}
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Kanban Board */}
      <div className="flex-1 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <span className="text-sm text-gray-500">Loading...</span>
          </div>
        ) : (
          <KanbanBoard
            issues={issues}
            users={users}
            onSelectIssue={handleSelectIssue}
            onIssueUpdated={loadIssues}
            onOptimisticStatusChange={handleOptimisticStatusChange}
          />
        )}
      </div>

      {/* Issue Side Panel */}
      {selectedIssueKey && (
        <IssuePanel
          issueKey={selectedIssueKey}
          users={users}
          currentUser={currentUser}
          onClose={handleClosePanel}
          onUpdated={loadIssues}
          onDeleted={handleIssueDeleted}
        />
      )}
    </div>
  );
}
